import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  fetchAgents,
  fetchCompanyBranches,
  fetchCompanyTeams,
  updateAgentTeams,
} from './services';

// A first page replaces the list, a later page is appended, and a silent
// refetch swaps one already loaded page in place.
const requestKind = ({ page = 1, silent } = {}) => {
  if (silent) return 'refresh';
  return page > 1 ? 'more' : 'list';
};

const isCurrentRequest = (state, action) =>
  state.requestIds[requestKind(action.meta.arg)] === action.meta.requestId;

const normalizeAgents = (results) =>
  (results || []).map((agent) => ({ ...agent, teams: agent.teams || [] }));

const setAgentTeams = (state, { agentId, teams }) => {
  const agent = state.agents.find(({ id }) => id === agentId);
  if (agent) agent.teams = teams;
};

export const AgentManagementSlice = createSlice({
  name: 'AgentManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state, action) => {
        const kind = requestKind(action.meta.arg);
        state.requestIds[kind] = action.meta.requestId;

        if (kind === 'more') {
          state.loadingMore = true;
          state.loadMoreFailed = false;
          return;
        }
        if (kind === 'refresh') return;

        // A new list makes any pending page or refetch of the old one stale.
        state.requestIds.more = null;
        state.requestIds.refresh = null;
        state.listStatus = 'loading';
        state.error = null;
        state.agents = [];
        state.page = 0;
        state.loadingMore = false;
        state.loadMoreFailed = false;
        state.reachedEnd = false;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        if (!isCurrentRequest(state, action)) return;

        const {
          results,
          count,
          available_agents_in_plan: availableAgents,
          total_active: totalActive,
          total_active_filtered: totalActiveFiltered,
          total_inactive_filtered: totalInactiveFiltered,
        } = action.payload;
        const { page = 1 } = action.meta.arg;
        const agents = normalizeAgents(results);
        const kind = requestKind(action.meta.arg);

        if (kind === 'list') {
          state.listStatus = 'succeeded';
          state.agents = agents;
          state.page = 1;
        } else if (kind === 'more') {
          // Agents created between pages shift the offsets, so a row can
          // come back again on the next page.
          const loadedIds = new Set(state.agents.map(({ id }) => id));
          const newAgents = agents.filter(({ id }) => !loadedIds.has(id));
          state.agents.push(...newAgents);
          state.page = page;
          // Agents removed between pages leave `count` above what can be
          // loaded, so a page that adds nothing stops the infinite scroll.
          state.reachedEnd = newAgents.length === 0;
          state.loadingMore = false;
        } else {
          state.agents.splice(
            (page - 1) * state.pageSize,
            state.pageSize,
            ...agents
          );
        }

        state.count = count ?? 0;
        if (availableAgents !== undefined)
          state.availableAgents = availableAgents;
        if (totalActive !== undefined) state.totalActive = totalActive;
        if (totalActiveFiltered !== undefined)
          state.totalActiveFiltered = totalActiveFiltered;
        if (totalInactiveFiltered !== undefined)
          state.totalInactiveFiltered = totalInactiveFiltered;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        if (!isCurrentRequest(state, action)) return;

        const kind = requestKind(action.meta.arg);
        if (kind === 'refresh') return;
        if (kind === 'more') {
          state.loadingMore = false;
          state.loadMoreFailed = true;
          return;
        }

        state.listStatus = 'failed';
        state.agents = [];
        state.error = action.error?.message || 'Failed to fetch agents';
      })
      .addCase(fetchCompanyBranches.pending, (state) => {
        state.branchesStatus = 'loading';
      })
      .addCase(fetchCompanyBranches.fulfilled, (state, action) => {
        state.branchesStatus = 'succeeded';
        state.branches = (action.payload.results || []).map(({ id, name }) => ({
          id,
          name,
        }));
        state.branchesCount = state.branches.length;
      })
      .addCase(fetchCompanyBranches.rejected, (state) => {
        state.branchesStatus = 'failed';
        state.branches = [];
        state.branchesCount = 0;
      })
      .addCase(fetchCompanyTeams.pending, (state) => {
        state.teamsStatus = 'loading';
      })
      .addCase(fetchCompanyTeams.fulfilled, (state, action) => {
        state.teamsStatus = 'succeeded';
        state.teams = (action.payload.results || []).map(({ id, name }) => ({
          id,
          name,
        }));
      })
      .addCase(fetchCompanyTeams.rejected, (state) => {
        state.teamsStatus = 'failed';
        state.teams = [];
      })
      .addCase(updateAgentTeams.fulfilled, (state, action) => {
        setAgentTeams(state, action.payload);
      })
      .addCase(updateAgentTeams.rejected, (state, action) => {
        if (action.payload) setAgentTeams(state, action.payload);
      });
  },
});

export default AgentManagementSlice.reducer;
